import { Component } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Table } from '../../../shared/table/table';
import { AppIcons } from '../../../../core/icons/fa-icons';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { TableAction, TableColumn } from '../../../shared/models/table-models';
import { Setup } from '../../shared/service/setup';

;

@Component({
  selector: 'app-incom-type',
  imports: [TranslatePipe, Table, Popup],
  templateUrl: './incom-type.html',
  styleUrl: './incom-type.css',
})
export class IncomType {
  icons = AppIcons;

  list: any[] = [];
  columns: TableColumn[] = [];
  fields: DynamicFieldConfig[] = [];

  showPopup = false;
  isEditMode = false;
  isViewMode = false;
  formData: any = {};

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private setup: Setup,
    private toastr: ToastrService,
    private picklist: PickListService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadPicklists();
  }

  // ================= PICKLIST =================
  loadPicklists(): void {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.buildFields();
      this.buildColumns();
      this.loadData();
    });
  }

  // ================= FORM =================
  buildFields(): void {
    this.fields = [
      {
        key: 'stmntTypSeq',
        label: 'SETUP.ID',
        type: 'text',
        hidden: true,
      },
      {
        key: 'name',
        label: 'SETUP.INCOME.TYPE_TITLE',
        type: 'text',
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: { required: true },
      },
      {
        key: 'description',
        label: 'COMMON.DESCRIPTION',
        type: 'textarea',
      },
    ];
  }

  // ================= TABLE =================
  buildColumns(): void {
    this.columns = [
      {
        key: 'stmntTypSeq',
        label: 'SETUP.ID',
      },
      {
        key: 'name',
        label: 'SETUP.INCOME.TYPE_TITLE',
        type: 'link',
        onClick: (row: any) => this.goToIncomeStatementValue(row),
      },
      {
        key: 'description',
        label: 'COMMON.DESCRIPTION',
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: this.getStatusMap(),
        badgeMap: this.getStatusBadgeMap(),
      },
    ];
  }

  goToIncomeStatementValue(row: any): void {
    this.router.navigate(['/income-statement-value', row.stmntTypSeq]);
  }

  getStatusMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    const map: Record<number, string> = {};

    options.forEach((option) => {
      map[option.value] = option.label;
    });

    return map;
  }

  getStatusBadgeMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    // Rows always carry 706 (active) / 707 (inactive), so the pill colour must not depend on the picklist label text.
    const map: Record<number, string> = { 706: 'status-active', 707: 'status-inactive' };

    options.forEach((option) => {
      const label = option.label?.trim().toLowerCase();

      if (label === 'active') {
        map[option.value] = 'status-active';
      } else if (label === 'inactive') {
        map[option.value] = 'status-inactive';
      } else if (!map[option.value]) {
        map[option.value] = 'status-default';
      }
    });

    return map;
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.view,
      tooltip: 'COMMON.VIEW',
      class: 'action-view',
      onClick: (row) => this.openPopup(row, true),
    },
    {
      icon: this.icons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
    },
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.name });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.delete(this.selectedRow);
    this.selectedRow = null;
  }

  // ================= LOAD =================
  loadData(): void {
    this.setup.getAllIncomeStatementTypes().subscribe({
      next: (res: any[]) => {
        this.list = (res || []).map((item) => ({
          stmntTypSeq: item.stmntTypSeq,
          name: item.typNm,
          description: item.dscr,
          status: item.actvFlg ? 706 : 707,
        }));
      },
      error: () => {
        this.toastr.error(this.translate.instant('SETUP.INCOME.LOAD_TYPES_FAILED'));
      },
    });
  }

  // ================= POPUP =================
  openPopup(data?: any, viewMode: boolean = false): void {
    this.showPopup = true;
    this.isViewMode = viewMode;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        stmntTypSeq: data.stmntTypSeq,
        name: data.name,
        description: data.description,
        status: data.status,
      };

      return;
    }

    this.isEditMode = false;
    this.formData = {
      stmntTypSeq: null,
      status: this.getDefaultStatus(),
    };
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || 706;
  }

  // ================= SAVE =================
  handleSave = (form: any) => {
    const payload = {
      stmntTypSeq: form.stmntTypSeq ?? null,
      typNm: form.name,
      dscr: form.description || '',
      actvFlg: Number(form.status) === 706,
      delFlg: 0,
    };

    return this.setup.saveIncomeStatementType(payload).subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant(this.isEditMode
            ? 'SETUP.INCOME.TYPE_UPDATED'
            : 'SETUP.INCOME.TYPE_CREATED')
        );

        this.showPopup = false;
        this.loadData();
      },
      error: () => {
        this.toastr.error(this.translate.instant('SETUP.INCOME.SAVE_TYPE_FAILED'));
      },
    });
  };

  // ================= DELETE =================
  delete(row: any): void {
    const payload = {
      stmntTypSeq: row.stmntTypSeq,
      typNm: row.name,
      dscr: row.description || '',
      actvFlg: Number(row.status) === 706,
      delFlg: 1,
    };

    this.setup.saveIncomeStatementType(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('SETUP.INCOME.TYPE_DELETED'));
        this.loadData();
      },
      error: () => {
        this.toastr.error(this.translate.instant('SETUP.INCOME.DELETE_TYPE_FAILED'));
      },
    });
  }
}